import { useNavigationState } from "@/zustand/NavigationState";
import { ChatType } from "@/server/types/chat";
import { MemberRole } from "@/server/types/member";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@primitives/dropdown-menu";

import {
  VolumeOffIcon,
  Volume2Icon,
  Ellipsis,
  Trash2,
  LogOut,
  Info,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import DeleteP2PChat from "@/components/shared/dialogs/DeleteP2PChat";
import DeleteGroupChat from "@/components/shared/dialogs/DeleteGroupChat";
import LeaveGroupChat from "@/components/shared/dialogs/LeaveGroupChat";

type DesktopChatMenuProps = {
  onMute: () => void;
  onUnMute: () => void;
};

export default function DesktopChatMenu({
  onMute,
  onUnMute,
}: DesktopChatMenuProps) {
  const { t } = useTranslation();
  const activeChatMuted = useNavigationState((state) => state.activeChatMuted);
  const activeChatId = useNavigationState((state) => state.activeChatId);

  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const toggleChatDetails = useNavigationState(
    (state) => state.toggleChatDetails,
  );

  const [openDialog, setOpenDialog] = useState<
    "delete-p2p-chat" | "delete-group-chat" | "leave-group-chat" | null
  >(null);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger className="flex shrink-0 items-center justify-center">
          <Ellipsis
            className="shrink-0 border-none outline-none"
            size={20}
            strokeWidth={2}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="flex flex-col gap-0">
          {activeChatMuted ? (
            <DropdownMenuItem onClick={onUnMute}>
              <VolumeOffIcon className="mr-2 size-4" />
              {t("unmute")}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={onMute}>
              <Volume2Icon className="mr-2 size-4" />
              {t("mute")}
            </DropdownMenuItem>
          )}

          <DropdownMenuItem onClick={() => toggleChatDetails()}>
            <Info className="mr-2 size-4" />
            {t("chatInfo")}
          </DropdownMenuItem>

          {activeChatType === ChatType.P2P && (
            <DropdownMenuItem
              // onClick={handleDeleteP2PChat}
              onSelect={(e) => {
                e.preventDefault();
                setOpenDialog("delete-p2p-chat");
              }}
              className="text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {t("delete")}
            </DropdownMenuItem>
          )}

          {activeChatType === ChatType.GROUP &&
            (activeChatRoleAsMember === MemberRole.OWNER ? (
              <DropdownMenuItem
                // onClick={handleDeleteGroupChat}
                onSelect={(e) => {
                  e.preventDefault();
                  setOpenDialog("delete-group-chat");
                }}
                className="text-destructive"
              >
                <Trash2 className="mr-2 size-4" />
                {t("deleteGroup")}
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                // onClick={handleLeaveChat}
                onSelect={(e) => {
                  e.preventDefault();
                  setOpenDialog("leave-group-chat");
                }}
                className="text-destructive"
              >
                <LogOut className="mr-2 size-4" />
                {t("leave")}
              </DropdownMenuItem>
            ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* CONFIRMATION DIALOGS */}

      <DeleteP2PChat
        chatId={activeChatId || ""}
        open={openDialog === "delete-p2p-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-p2p-chat" : null);
        }}
      />

      <DeleteGroupChat
        chatId={activeChatId || ""}
        open={openDialog === "delete-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-group-chat" : null);
        }}
      />

      <LeaveGroupChat
        chatId={activeChatId || ""}
        open={openDialog === "leave-group-chat"}
        onOpenClose={(state) => {
          setOpenDialog(state ? "delete-group-chat" : null);
        }}
      />
    </>
  );
}
